// ============================================================================
// APPEARANCE ENGINE
// Deterministic styling system for Meetoi Digital Cards
// ============================================================================

export const ACCENTS = {
  blue: '#2563eb',
  green: '#16a34a',
  purple: '#9333ea',
  orange: '#ea580c',
  red: '#dc2626',
  teal: '#0d9488',
  indigo: '#4f46e5',
  grey: '#475569'
};

export const DEFAULT_APPEARANCE = {
  theme: 'classic_light',
  accent: 'blue',
  font: 'modern_sans',
  corners: 'rounded',
  density: 'comfortable',
  mode: 'light'
};

export const THEMES = {
  classic_light: {
    name: 'Classic Light',
    light: { pageBg: 'bg-slate-100', cardBg: 'bg-white', textMain: 'text-slate-900', textMuted: 'text-slate-500', border: 'border-slate-200', elementBg: 'bg-slate-50', elementHover: 'hover:bg-slate-100' },
    dark: { pageBg: 'bg-slate-950', cardBg: 'bg-slate-900', textMain: 'text-white', textMuted: 'text-slate-400', border: 'border-slate-800', elementBg: 'bg-slate-800', elementHover: 'hover:bg-slate-700' }
  },
  midnight: {
    name: 'Midnight',
    light: { pageBg: 'bg-slate-200', cardBg: 'bg-slate-800', textMain: 'text-white', textMuted: 'text-slate-300', border: 'border-slate-700', elementBg: 'bg-slate-700', elementHover: 'hover:bg-slate-600' },
    dark: { pageBg: 'bg-black', cardBg: 'bg-slate-900', textMain: 'text-white', textMuted: 'text-slate-400', border: 'border-slate-800', elementBg: 'bg-slate-800', elementHover: 'hover:bg-slate-700' }
  },
  ocean: {
    name: 'Ocean',
    light: { pageBg: 'bg-sky-100', cardBg: 'bg-sky-50', textMain: 'text-sky-950', textMuted: 'text-sky-700', border: 'border-sky-200', elementBg: 'bg-white', elementHover: 'hover:bg-sky-100' },
    dark: { pageBg: 'bg-sky-950', cardBg: 'bg-sky-900', textMain: 'text-sky-50', textMuted: 'text-sky-300', border: 'border-sky-800', elementBg: 'bg-sky-950', elementHover: 'hover:bg-sky-800' }
  },
  forest: {
    name: 'Forest',
    light: { pageBg: 'bg-emerald-100', cardBg: 'bg-emerald-50', textMain: 'text-emerald-950', textMuted: 'text-emerald-700', border: 'border-emerald-200', elementBg: 'bg-white', elementHover: 'hover:bg-emerald-100' },
    dark: { pageBg: 'bg-emerald-950', cardBg: 'bg-emerald-900', textMain: 'text-emerald-50', textMuted: 'text-emerald-300', border: 'border-emerald-800', elementBg: 'bg-emerald-950', elementHover: 'hover:bg-emerald-800' }
  },
  sunset: {
    name: 'Sunset',
    light: { pageBg: 'bg-orange-100', cardBg: 'bg-orange-50/50', textMain: 'text-stone-900', textMuted: 'text-stone-500', border: 'border-orange-200', elementBg: 'bg-white', elementHover: 'hover:bg-orange-100' },
    dark: { pageBg: 'bg-stone-950', cardBg: 'bg-stone-900', textMain: 'text-stone-50', textMuted: 'text-stone-400', border: 'border-stone-800', elementBg: 'bg-stone-950', elementHover: 'hover:bg-stone-800' }
  },
  minimal: {
    name: 'Minimal',
    light: { pageBg: 'bg-gray-100', cardBg: 'bg-white', textMain: 'text-black', textMuted: 'text-gray-500', border: 'border-gray-200', elementBg: 'bg-white', elementHover: 'hover:bg-gray-50' },
    dark: { pageBg: 'bg-zinc-950', cardBg: 'bg-black', textMain: 'text-white', textMuted: 'text-zinc-400', border: 'border-zinc-800', elementBg: 'bg-black', elementHover: 'hover:bg-zinc-900' }
  }
};

export const FONTS = {
  modern_sans: { name: 'Modern Sans', class: 'font-sans' },
  classic_sans: { name: 'Classic Sans', class: 'font-["Helvetica_Neue",Helvetica,Arial,sans-serif]' },
  elegant_serif: { name: 'Elegant Serif', class: 'font-serif' }
};

export const CORNERS = {
  rounded: { name: 'Rounded', card: 'rounded-[2.5rem]', element: 'rounded-xl', button: 'rounded-xl', avatar: 'rounded-full' },
  square: { name: 'Square', card: 'rounded-none', element: 'rounded-none', button: 'rounded-none', avatar: 'rounded-none' }
};

export const DENSITIES = {
  comfortable: { name: 'Comfortable', gap: 'space-y-4', px: 'px-6', py: 'py-4', headerPt: 'pt-8', pb: 'pb-8', avatarSize: 'w-32 h-32' },
  compact: { name: 'Compact', gap: 'space-y-2.5', px: 'px-5', py: 'py-3', headerPt: 'pt-6', pb: 'pb-6', avatarSize: 'w-24 h-24' }
};

export const getAppearanceStyles = (appearance) => {
  const app = { ...DEFAULT_APPEARANCE, ...(appearance || {}) };
  
  const theme = THEMES[app.theme] ? THEMES[app.theme][app.mode] : THEMES.classic_light.light;
  const font = FONTS[app.font] ? FONTS[app.font].class : FONTS.modern_sans.class;
  const corner = CORNERS[app.corners] || CORNERS.rounded;
  const density = DENSITIES[app.density] || DENSITIES.comfortable;
  const accentHex = ACCENTS[app.accent] || ACCENTS.blue;

  return {
    ...theme,
    fontClass: font,
    cornerCard: corner.card,
    cornerElement: corner.element,
    cornerButton: corner.button,
    cornerAvatar: corner.avatar,
    gap: density.gap,
    px: density.px,
    py: density.py,
    headerPt: density.headerPt,
    pb: density.pb,
    avatarSize: density.avatarSize,
    accentHex
  };
};